import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";

export default function CartPage() {
  const { items, subtotal, shipping, total, removeItem, updateQty } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div data-testid="cart-page-empty" className="max-w-3xl mx-auto px-6 py-28 text-center">
        <div className="label-xs mb-3">Cart</div>
        <h1 className="font-display text-5xl md:text-6xl font-medium mb-4">
          Your cart is empty
        </h1>
        <p className="text-neutral-600 mb-8">
          Start building your wardrobe — all orders over $100 ship free.
        </p>
        <Link
          to="/shop"
          data-testid="cart-empty-shop-link"
          className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white text-xs uppercase tracking-[0.25em]"
        >
          Shop the collection <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="label-xs mb-3">Cart</div>
      <h1 className="font-display text-5xl md:text-6xl font-medium leading-none mb-10">
        Your Cart ({items.length})
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-8 divide-y divide-[color:var(--ssm-border)] border-y border-[color:var(--ssm-border)]">
          {items.map((item) => (
            <div
              key={`${item.product_id}-${item.size}-${item.color || "default"}`}
              data-testid={`cart-page-item-${item.slug}`}
              className="flex gap-6 py-6"
            >
              <Link to={`/product/${item.slug}`} className="shrink-0">
                <img src={item.image} alt={item.name} className="w-24 h-32 md:w-28 md:h-36 object-cover bg-neutral-100" />
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between gap-4">
                  <Link to={`/product/${item.slug}`} className="font-medium hover:underline">
                    {item.name}
                  </Link>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
                <div className="text-sm text-neutral-500 mt-1">
                  {item.color ? `${item.color} · ` : ""}Size {item.size}
                </div>
                <div className="text-sm text-neutral-500">${item.price.toFixed(2)} each</div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="inline-flex items-center border border-[color:var(--ssm-border)]">
                    <button
                      onClick={() => updateQty(item.product_id, item.size, item.quantity - 1, item.color)}
                      className="p-2"
                      aria-label="Decrease"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="px-4 text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateQty(item.product_id, item.size, item.quantity + 1, item.color)}
                      className="p-2"
                      aria-label="Increase"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(item.product_id, item.size, item.color)}
                    className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1"
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-[color:var(--ssm-surface)] p-8 space-y-4">
            <h2 className="font-display text-2xl font-medium">Order Summary</h2>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Shipping</span>
              <span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between text-base font-medium pt-3 border-t border-[color:var(--ssm-border)]">
              <span>Total</span>
              <span data-testid="cart-page-total">${total.toFixed(2)}</span>
            </div>
            <button
              data-testid="cart-page-checkout-btn"
              onClick={() => navigate("/checkout")}
              className="w-full py-4 bg-black text-white text-xs uppercase tracking-[0.25em] hover:bg-neutral-800 transition-colors"
            >
              Proceed to Checkout
            </button>
            <p className="text-[11px] text-neutral-500 text-center">
              Secure checkout · Encrypted payments · Powered by Stripe
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
